/* eslint-disable react/forbid-prop-types */
// Refactored by j-funk into functional component 2023/09
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Alert, Row, Col, Tabs, Tab } from 'react-bootstrap';
import * as d3 from 'd3';
import { connect } from 'react-redux';
import { useMeasure } from 'react-use';
// eslint-disable-next-line css-modules/no-unused-class
import s from './NetworkTabs.css';

import TermEnrichment from '../TermEnrichment';
import GenePredictions from '../GenePredictions';
import GeneDetail from '../GeneDetail';
import EdgeDetail from '../EdgeDetail';
import Tutorial from '../Tutorial';

import { fetchTermPredictions } from '../../actions/PredictionActions';

import {
  selectEdge,
  selectNode,
  fetchNetworkWithTerms,
  addGeneset,
  removeGeneset,
} from '../../actions/NetworkActions';

import { setGenes } from '../../actions/actions';

import { TISSUE_DB } from '../../settings';

function NetworkTabsView(props) {
  const {
    annotatedTerms,
    columns,
    // eslint-disable-next-line react/prop-types
    datatypes,
    edgeNetworks,
    evidence,
    genePredictions,
    // eslint-disable-next-line react/prop-types
    genes,
    geneSets,
    integration,
    onGeneAdd,
    onGeneRemove,
    onGeneSelect,
    onTermDeselect,
    onTermSelect,
    selectedEdge,
    selectedGene,
    termDatabases,
    termPredictions,
    // eslint-disable-next-line react/prop-types
    visibleGenes,
  } = props;
  const [divRef, { width: containerWidth }] = useMeasure();
  const networkTabs = useRef(null);
  const [selectedTab, setSelectedTab] = useState(1);
  const [tissueTerms, setTissueTerms] = useState([]);
  const [functionTerms, setFunctionTerms] = useState([]);

  useEffect(() => {
    if (columns !== 2 && (selectedEdge || selectedGene)) setSelectedTab(4);
  }, [columns, selectedGene, selectedEdge]);

  useEffect(() => {
    if (!annotatedTerms) return;
    setTissueTerms(
      annotatedTerms.filter(term => term.database.slug === TISSUE_DB),
    );
    setFunctionTerms(
      annotatedTerms.filter(term => term.database.slug !== TISSUE_DB),
    );
  }, [annotatedTerms]);

  // The top connected genes to the query
  const networkGenes = useMemo(
    () => (
      <GenePredictions
        title={
          <h4 className={s.h4}>
            Functionally related genes
            <br />
            <small>Top connected genes to the query</small>
          </h4>
        }
        predictions={genePredictions}
        highlightGenes={visibleGenes}
        containerWidth={containerWidth / columns}
        onGeneSelect={gene => {
          onGeneSelect(gene, termDatabases);
        }}
        onGeneRemove={gene => {
          onGeneRemove(gene, genes, integration, datatypes);
        }}
        onGeneAdd={gene => {
          onGeneAdd(gene, genes, integration, datatypes);
        }}
      />
    ),
    [
      columns,
      containerWidth,
      datatypes,
      genePredictions,
      genes,
      integration,
      onGeneAdd,
      onGeneRemove,
      onGeneSelect,
      termDatabases,
      visibleGenes,
    ],
  );

  // Terms that are enriched in the visible network
  const enrichment = useMemo(
    () => (
      <TermEnrichment
        terms={functionTerms}
        selectedGenes={visibleGenes}
        title={
          <h4 className={s.h4}>
            Enriched function
            <br />
            <small>
              Biological processes and diseases represented in the network
            </small>
          </h4>
        }
        onTermSelect={term => onTermSelect(term, geneSets)}
        onTermDeselect={onTermDeselect}
        geneSets={geneSets}
        containerWidth={containerWidth / columns}
      />
    ),
    [
      columns,
      containerWidth,
      functionTerms,
      geneSets,
      onTermDeselect,
      onTermSelect,
      visibleGenes,
    ],
  );

  const tissue = useMemo(
    () => (
      <TermEnrichment
        terms={tissueTerms}
        selectedGenes={visibleGenes}
        calculateEnrichment={false}
        title={
          <h4 className={s.h4}>
            Tissue expression
            <br />
            <small>Tissue expression of genes in the network</small>
          </h4>
        }
        onTermSelect={term => onTermSelect(term, geneSets)}
        onTermDeselect={onTermDeselect}
        geneSets={geneSets}
        containerWidth={containerWidth / columns}
      />
    ),
    [
      columns,
      containerWidth,
      geneSets,
      onTermDeselect,
      onTermSelect,
      tissueTerms,
      visibleGenes,
    ],
  );

  // Show edge detail if the user selected an edge
  const edgeDetail = useMemo(
    () =>
      selectedEdge ? (
        <EdgeDetail
          edge={selectedEdge}
          evidence={evidence}
          edgeNetworks={edgeNetworks}
          termDatabases={termDatabases}
          networkTitle={integration.context.title}
          containerWidth={containerWidth / columns}
          onGeneSelect={onGeneSelect}
        />
      ) : null,
    [
      columns,
      containerWidth,
      edgeNetworks,
      evidence,
      integration.context.title,
      onGeneSelect,
      selectedEdge,
      termDatabases,
    ],
  );

  // Show gene detail if the user selected a gene
  const geneDetail = useMemo(
    () =>
      selectedGene ? (
        <GeneDetail
          gene={selectedGene}
          termPredictions={termPredictions}
          termDatabases={termDatabases}
        />
      ) : null,
    [selectedGene, termDatabases, termPredictions],
  );

  const detail = (
    <Alert style={{ margin: '20px 10px', textAlign: 'center' }}>
      <i className="fa fa-info-circle" /> Click on an edge or node in the
      network to view additional detail.
    </Alert>
  );

  const detailTab = (
    <Tab eventKey={4} title="Detail">
      {geneDetail}
      {edgeDetail}
      {!geneDetail && !edgeDetail ? detail : null}
    </Tab>
  );

  let tabpanels = (
    <Tabs
      ref={networkTabs}
      id="network"
      defaultActiveKey={1}
      activeKey={selectedTab}
      onSelect={tabid => {
        setSelectedTab(tabid);
      }}
      bsStyle="tabs"
    >
      <Tab eventKey={1} title="Genes">
        {networkGenes}
      </Tab>
      <Tab eventKey={2} title="Process">
        {enrichment}
      </Tab>
      <Tab eventKey={3} title="Tissue">
        {tissue}
      </Tab>
      {columns === 2 ? null : detailTab}
    </Tabs>
  );

  // Render as a row with two columns
  if (columns === 2) {
    tabpanels = (
      <Row>
        <Col md={6}>{tabpanels}</Col>
        <Col md={6}>
          {geneDetail}
          {edgeDetail}
          {!geneDetail && !edgeDetail ? detail : null}
        </Col>
      </Row>
    );
  }

  return (
    <div ref={divRef}>
      {tabpanels}
      <Tutorial
        step={5}
        target={networkTabs.current}
        onNext={() => setSelectedTab(2)}
        container={this}
        placement="left"
        title="Explore similar genes"
        text="The genes with the highest average edge weight to the query genes,
         and thus more likely to be functionally similar, are displayed here."
      />
      <Tutorial
        step={6}
        target={networkTabs.current}
        onPrev={() => setSelectedTab(1)}
        onNext={() => setSelectedTab(3)}
        container={this}
        placement="left"
        title="Explore biological processes"
        text='The "Process" tab lists the represented (enriched) biological processes and
         diseases in the displayed network. Select a process to highlight the
          associated genes in the network.'
      />
      <Tutorial
        step={7}
        target={networkTabs.current}
        onPrev={() => setSelectedTab(2)}
        onNext={() => setSelectedTab(4)}
        container={this}
        placement="left"
        title="Explore tissues"
        text='The "Tissue" tab lists the tissues that the genes in the network are expressed.
         Select a tissue to highlight the expressed genes in the network.'
      />
      <Tutorial
        step={8}
        target={networkTabs.current}
        onPrev={() => setSelectedTab(3)}
        container={this}
        placement="left"
        end
        title="Explore detailed view"
        text='The "Detail" tab shows additional information for a selected edge or node from the network.
         For example, selecting an edge in the network will show the datasets that contributed to
          the associated prediction probability.'
      />
    </div>
  );
}

NetworkTabsView.propTypes = {
  // Identifer for keeping track of network in global state store
  // {group, idx}
  // eslint-disable-next-line react/no-unused-prop-types
  id: PropTypes.object.isRequired,

  // Integration context
  integration: PropTypes.object.isRequired,

  // Data of vertices and edges corresponding to the network
  genePredictions: PropTypes.array.isRequired,

  // Edge or node selected by the user
  selectedEdge: PropTypes.object,
  selectedGene: PropTypes.object,

  // Array of genesets to highlight in the network
  geneSets: PropTypes.object,

  // Number of columns
  columns: PropTypes.number,

  // Dataset evidence for the selected edge
  evidence: PropTypes.object,

  // Terms that the visible genes are annotated to
  annotatedTerms: PropTypes.array,

  // Selected edge across multiple networks
  edgeNetworks: PropTypes.array,

  // Object of terms predicted for various genes
  termPredictions: PropTypes.object,

  // Databases of term predictions
  termDatabases: PropTypes.array,

  // Callback when a term is selected
  onTermSelect: PropTypes.func,

  // Callback when a term is de-selected
  onTermDeselect: PropTypes.func,

  // Callback when a gene is selected
  onGeneSelect: PropTypes.func,

  // Callback when a gene is added to the query set
  onGeneAdd: PropTypes.func,

  // Callback when a gene is removed from the query set
  onGeneRemove: PropTypes.func,
};

function mapStateToProps(state, props) {
  const { group, idx } = props.id;
  const network = state.networkGroups[group][idx];
  return {
    integration: network.selectedIntegration,
    datatypes: network.selectedDatatypes,
    genes: network.selectedGenes,

    selectedEdge: network.selectedEdge,
    selectedGene: network.selectedNode,

    geneSets: state.geneSets,

    evidence: network.selectedEdge && network.evidence[network.selectedEdge.id],
    termDatabases: state.termDatabases,
    termPredictions:
      network.selectedNode &&
      state.termPredictions[network.selectedNode.entrez],

    annotatedTerms: network.annotatedTerms,

    edgeNetworks:
      network.selectedEdge && network.edgeNetworks[network.selectedEdge.id],
  };
}

function mapDispatchToProps(dispatch, props) {
  const { id } = props;
  return {
    onGeneSelect: (d, termDatabases) => {
      dispatch(selectEdge(null, id));
      dispatch(selectNode(d, id));
      Object.keys(termDatabases).forEach(db => {
        dispatch(fetchTermPredictions(d, termDatabases[db].slug));
      });
    },
    onTermSelect: (term, geneSets) => {
      // Calculate the maximum geneset id (all color up to that index
      // could be used already)
      const color = d3.scaleOrdinal(d3.schemeCategory10).range()[
        (Math.max(...Object.keys(geneSets).map(gs => geneSets[gs].id), -1) +
          1) %
          10
      ];
      const geneset = {
        genes: term.annotations,
        title: term.title,
        identifier: term.identifier,
        id: term.identifier,
        color,
      };
      dispatch(addGeneset(geneset, term.identifier));
    },
    onTermDeselect: term => {
      dispatch(removeGeneset(term.identifier));
    },
    onGeneAdd: (gene, genes, integration, datatypes) => {
      const newGenes = genes.slice(0);
      newGenes.push(gene);
      dispatch(setGenes(newGenes, id));
      dispatch(fetchNetworkWithTerms(integration, newGenes, id, datatypes));
    },
    onGeneRemove: (gene, genes, integration, datatypes) => {
      const newGenes = genes.filter(g => g.entrez !== gene.entrez);
      dispatch(setGenes(newGenes, id));
      dispatch(fetchNetworkWithTerms(integration, newGenes, id, datatypes));
    },
  };
}

const NetworkTabs = connect(
  mapStateToProps,
  mapDispatchToProps,
)(NetworkTabsView);

export default withStyles(s)(NetworkTabs);
